<script lang="ts" setup>
import { ref, computed } from 'vue';
import { UploadType } from '@/gql/graphql';
import { BAlert, BButton, BModal, BSpinner } from 'bootstrap-vue-next';
import { useUploadsStore } from '@/store/uploads';

const uploadsStore = useUploadsStore();

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  upload: UploadType;
}>();

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

// --- Component state ---
const show = computed<boolean>(() => {
  return props.show;
});

// --- Abort Progress Indicators ---
const abortInProgress = ref<boolean>(false);
const abortError = ref<string | null>(null);
const abortShowSuccess = ref<boolean>(false);

// --- Handle component actions ---
async function onAbortConfirmed() {
  if (!props.upload.id) {
    abortError.value = 'Upload ID for abortion not provided';
    console.error('Upload ID for abortion not provided');
    return;
  }

  // stop upload immediately and update the status
  abortInProgress.value = true;
  try {
    // abort the upload handled by uploadsStore
    await uploadsStore.abortUpload(props.upload.id);
    // show success indicator
    abortShowSuccess.value = true;
    setTimeout(() => {
      abortShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } catch (err: unknown) {
    if (err instanceof Error) {
      abortError.value = `Error updating upload status to aborted: ${err.message}`;
    } else {
      abortError.value = 'Error updating upload status to aborted';
    }
  } finally {
    abortInProgress.value = false;
  }
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <div>
    <!-- MODAL: Abort Upload -->
    <b-modal v-model="show" @hide="handleCloseModal">
      <!-- Title -->
      <template #title>
        <font-awesome-icon icon="times" class="me-2" />
        Abort upload
      </template>
      <!-- Content -->
      <div>
        <!-- Abort? Confirmation Message -->
        <p>
          Are you sure you want to abort the upload of
          <strong>{{ props.upload.targetFileName }}</strong
          >?
        </p>

        <!-- Error Message -->
        <b-alert
          v-model="abortError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="abortError = null"
        >
          {{ abortError }}
        </b-alert>
      </div>
      <!-- Footer -->
      <template #footer>
        <b-button variant="secondary" @click="handleCloseModal">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          :disabled="abortInProgress || abortShowSuccess"
          @click="onAbortConfirmed"
        >
          <span v-if="abortInProgress">
            Requesting Abortion
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="abortShowSuccess">
            Abortion Requested
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Abort Upload </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
